import React, { useState, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    ListItemIcon,
    Switch,
    FormControlLabel,
    Collapse,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import CloseIcon from '@material-ui/icons/Close'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'

import { ColorPicker } from '../ColorPicker'
import {
    useUserTags,
    CreateUserTagRequest,
    UpdateUserTagRequest,
} from '../../contexts/UserTagsContext'
import { UserTag } from '../../models/Users/types'
import { Company } from '../../models/Company'
import { useUser } from '../../hooks/useUser'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'

interface PropertyTagsModalProps {
    open: boolean
    onClose: () => void
    companyList: Company[]
}

export const PropertyTagsModal: React.FC<PropertyTagsModalProps> = ({
    open,
    onClose,
    companyList,
}) => {
    const theme = useTheme()
    const { organization } = useUser()
    const {
        userTags,
        getUserTags,
        getGlobalTags,
        copyGlobalTag,
        createUserTag,
        updateUserTag,
        deleteUserTag,
        loading: contextLoading,
    } = useUserTags()

    const [globalTags, setGlobalTags] = useState<UserTag[]>([])
    const [globalTagsLoading, setGlobalTagsLoading] = useState(false)
    const [copyingTagId, setCopyingTagId] = useState<number | null>(null)
    const [globalTagsSectionOpen, setGlobalTagsSectionOpen] = useState(false)
    const [globalTagsSearch, setGlobalTagsSearch] = useState('')

    const [isCreating, setIsCreating] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editingTag, setEditingTag] = useState<UserTag | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [tagToDelete, setTagToDelete] = useState<UserTag | null>(null)

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        color: '#2196F3',
        apartment_ids: [] as number[],
        is_global_tag: false,
    })

    // All properties are available for assignment; an apartment can belong to any number of tags
    const getAvailableProperties = () => companyList

    // Apartment IDs the user has access to (for global tag copy count)
    const accessibleApartmentIds = useMemo(
        () => new Set(companyList.map((c) => c.id)),
        [companyList],
    )

    // Map apartment id -> Company for displaying property names
    const apartmentMap = useMemo(
        () => new Map(companyList.map((c) => [c.id, c])),
        [companyList],
    )

    // Which global tag's property list is expanded (null = none)
    const [expandedGlobalTagId, setExpandedGlobalTagId] = useState<
        number | null
    >(null)

    // Global tags filtered by search
    const filteredGlobalTags = useMemo(
        () =>
            globalTags.filter((tag) =>
                tag.name
                    .toLowerCase()
                    .includes(globalTagsSearch.trim().toLowerCase()),
            ),
        [globalTags, globalTagsSearch],
    )

    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [propertySearch, setPropertySearch] = useState('')

    useEffect(() => {
        if (open) {
            getUserTags()
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        setGlobalTagsLoading(true)
        getGlobalTags(organization ?? undefined)
            .then(setGlobalTags)
            .catch(() => setGlobalTags([]))
            .finally(() => setGlobalTagsLoading(false))
    }, [open, organization, getGlobalTags])

    const handleInputChange = (
        field: string,
        value: string | number[] | boolean,
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!formData.name.trim()) {
            newErrors.name = 'Tag name is required'
        }

        if (!formData.color) {
            newErrors.color = 'Color is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleCreateTag = async () => {
        if (!validateForm()) return

        try {
            const request: CreateUserTagRequest = {
                name: formData.name.trim(),
                color: formData.color,
                apartment_ids: formData.apartment_ids,
                is_global_tag: formData.is_global_tag,
            }
            await createUserTag(request)
            toast.success(`Tag "${formData.name.trim()}" created successfully!`)
            resetForm()
        } catch (error) {
            console.error('Error creating tag:', error)
            toast.error('Failed to create tag. Please try again.')
        }
    }

    const handleEditTag = async () => {
        if (!validateForm() || !editingTag) return

        try {
            const request: UpdateUserTagRequest = {
                id: editingTag.id,
                name: formData.name.trim(),
                color: formData.color,
                apartment_ids: formData.apartment_ids,
                is_global_tag: formData.is_global_tag,
            }
            await updateUserTag(request)
            toast.success(`Tag "${formData.name.trim()}" updated successfully!`)
            resetForm()
        } catch (error) {
            console.error('Error updating tag:', error)
            toast.error('Failed to update tag. Please try again.')
        }
    }

    const handleEditClick = (tag: UserTag) => {
        setEditingTag(tag)
        setFormData({
            name: tag.name,
            color: tag.color,
            apartment_ids: tag.apartments,
            is_global_tag: tag.is_global_tag ?? false,
        })
        setIsEditing(true)
    }

    const handleDeleteClick = (tag: UserTag) => {
        setTagToDelete(tag)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = async () => {
        if (!tagToDelete) return

        try {
            await deleteUserTag(tagToDelete.id)
            toast.success(`Tag "${tagToDelete.name}" deleted successfully!`)
            setDeleteDialogOpen(false)
            setTagToDelete(null)
        } catch (error) {
            console.error('Error deleting tag:', error)
            toast.error('Failed to delete tag. Please try again.')
        }
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setTagToDelete(null)
    }

    const resetForm = () => {
        setFormData({
            name: '',
            color: '#2196F3',
            apartment_ids: [],
            is_global_tag: false,
        })
        setErrors({})
        setPropertySearch('')
        setIsCreating(false)
        setIsEditing(false)
        setEditingTag(null)
    }

    const handleClose = () => {
        resetForm()
        setGlobalTagsSectionOpen(false)
        setGlobalTagsSearch('')
        setExpandedGlobalTagId(null)
        onClose()
    }

    const handleCopyGlobalTag = async (globalTag: UserTag) => {
        setCopyingTagId(globalTag.id)
        try {
            await copyGlobalTag(globalTag.id, organization ?? undefined)
            toast.success(
                `Tag "${globalTag.name}" copied to your tags. You can edit it to change properties.`,
            )
        } catch (err: unknown) {
            const ax = err as { response?: { data?: unknown; status?: number } }
            const data = ax.response?.data
            const status = ax.response?.status
            if (status === 400 && data && typeof data === 'object') {
                const d = data as {
                    key?: string
                    message?: string
                    detail?: string
                }
                if (
                    d.key === 'USER_TAG_NAME_EXISTS' ||
                    d.message?.includes('already exists')
                ) {
                    toast.error(
                        'You already have a tag with this name. Rename your existing tag or choose another global tag.',
                    )
                    return
                }
                if (typeof d.detail === 'string') {
                    toast.error(d.detail)
                    return
                }
            }
            toast.error('Failed to copy tag. Please try again.')
        } finally {
            setCopyingTagId(null)
        }
    }

    const handleSubmit = () => {
        if (isEditing) {
            handleEditTag()
        } else {
            handleCreateTag()
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: theme.shape.borderRadius * 3,
                    boxShadow: theme.shadows[10],
                    minHeight: '500px',
                },
            }}
        >
            <DialogTitle
                style={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: theme.palette.primary.contrastText,
                    padding: theme.spacing(3),
                    textAlign: 'center',
                    position: 'relative',
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: theme.spacing(1.5) }}
                >
                    <Typography
                        variant="h5"
                        component="div"
                        style={{ fontWeight: 600 }}
                    >
                        Property Tags
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        right: theme.spacing(1),
                        top: theme.spacing(1),
                        color: theme.palette.primary.contrastText,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent style={{ padding: theme.spacing(3) }}>
                {/* Header with Add Button */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginBottom: theme.spacing(2) }}
                >
                    <Typography variant="h6">Property Tags</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreating(true)}
                        disabled={isEditing}
                    >
                        Add Tag
                    </Button>
                </Box>

                {/* Info Text */}
                <Box
                    style={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius * 2,
                        padding: theme.spacing(2),
                        marginBottom: theme.spacing(3),
                        boxShadow: theme.shadows[1],
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        style={{ gap: theme.spacing(1.5) }}
                    >
                        <Box
                            style={{
                                backgroundColor: theme.palette.info.main,
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: 2,
                            }}
                        >
                            <Typography
                                variant="caption"
                                style={{
                                    color: theme.palette.info.contrastText,
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                }}
                            >
                                i
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                variant="body2"
                                style={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    marginBottom: theme.spacing(0.5),
                                }}
                            >
                                Property Tag Information
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.5,
                                }}
                            >
                                These tags are private and only visible to you.
                                A property can be assigned to any number of
                                tags.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Copy from global tags (collapsible) */}
                <Box
                    style={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        marginBottom: theme.spacing(3),
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        onClick={() =>
                            setGlobalTagsSectionOpen((prev) => !prev)
                        }
                        style={{
                            padding: theme.spacing(1.5, 2),
                            backgroundColor:
                                theme.palette.grey?.[100] ||
                                theme.palette.action.hover,
                            borderBottom: globalTagsSectionOpen
                                ? `1px solid ${theme.palette.divider}`
                                : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                        }}
                        role="button"
                        aria-expanded={globalTagsSectionOpen}
                    >
                        <Box>
                            <Typography
                                variant="subtitle2"
                                style={{ fontWeight: 600 }}
                            >
                                Copy from global tags
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Copy another user&apos;s global tag to create
                                your own. It will only include properties you
                                have access to.
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            aria-label={
                                globalTagsSectionOpen ? 'Collapse' : 'Expand'
                            }
                            style={{ marginLeft: theme.spacing(1) }}
                        >
                            {globalTagsSectionOpen ? (
                                <KeyboardArrowUpIcon />
                            ) : (
                                <KeyboardArrowDownIcon />
                            )}
                        </IconButton>
                    </Box>
                    <Collapse in={globalTagsSectionOpen} timeout="auto">
                        <Box
                            style={{
                                padding: theme.spacing(1.5, 2),
                                paddingTop: theme.spacing(1),
                            }}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search global tags..."
                                value={globalTagsSearch}
                                onChange={(e) =>
                                    setGlobalTagsSearch(e.target.value)
                                }
                                style={{
                                    marginBottom: theme.spacing(1.5),
                                }}
                                InputProps={{
                                    style: {
                                        borderRadius: theme.shape.borderRadius,
                                    },
                                }}
                            />
                            <Box
                                style={{
                                    maxHeight: '200px',
                                    overflow: 'auto',
                                }}
                            >
                                {globalTagsLoading ? (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        style={{
                                            padding: theme.spacing(3),
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : filteredGlobalTags.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            padding: theme.spacing(2),
                                        }}
                                    >
                                        {globalTagsSearch.trim()
                                            ? 'No global tags match your search.'
                                            : 'No global tags from other users available for your properties.'}
                                    </Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {filteredGlobalTags.map(
                                            (tag, index) => {
                                                const copyToApartmentIds = tag.apartments.filter(
                                                    (id) =>
                                                        accessibleApartmentIds.has(
                                                            id,
                                                        ),
                                                )
                                                const copyCount =
                                                    copyToApartmentIds.length
                                                const isExpanded =
                                                    expandedGlobalTagId ===
                                                    tag.id
                                                return (
                                                    <React.Fragment
                                                        key={tag.id}
                                                    >
                                                        <ListItem
                                                            style={{
                                                                borderRadius:
                                                                    theme.shape
                                                                        .borderRadius,
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                            disableGutters
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    setExpandedGlobalTagId(
                                                                        (
                                                                            prev,
                                                                        ) =>
                                                                            prev ===
                                                                            tag.id
                                                                                ? null
                                                                                : tag.id,
                                                                    )
                                                                }
                                                                aria-label={
                                                                    isExpanded
                                                                        ? 'Collapse properties'
                                                                        : 'Show properties'
                                                                }
                                                                style={{
                                                                    marginRight: theme.spacing(
                                                                        0.5,
                                                                    ),
                                                                }}
                                                            >
                                                                {isExpanded ? (
                                                                    <KeyboardArrowUpIcon fontSize="small" />
                                                                ) : (
                                                                    <KeyboardArrowDownIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                style={{
                                                                    gap: theme.spacing(
                                                                        1,
                                                                    ),
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                <Box
                                                                    style={{
                                                                        width: 16,
                                                                        height: 16,
                                                                        borderRadius:
                                                                            '50%',
                                                                        backgroundColor:
                                                                            tag.color,
                                                                        border: `1px solid ${theme.palette.divider}`,
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                                <ListItemText
                                                                    primary={
                                                                        tag.name
                                                                    }
                                                                    secondary={
                                                                        copyCount >
                                                                        0
                                                                            ? `Copies to ${copyCount} ${
                                                                                  copyCount ===
                                                                                  1
                                                                                      ? 'property'
                                                                                      : 'properties'
                                                                              }`
                                                                            : 'No properties you have access to'
                                                                    }
                                                                    primaryTypographyProps={{
                                                                        variant:
                                                                            'body2',
                                                                    }}
                                                                    secondaryTypographyProps={{
                                                                        variant:
                                                                            'caption',
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={
                                                                    copyingTagId ===
                                                                    tag.id ? (
                                                                        <CircularProgress
                                                                            size={
                                                                                14
                                                                            }
                                                                            color="inherit"
                                                                        />
                                                                    ) : (
                                                                        <FileCopyIcon />
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    handleCopyGlobalTag(
                                                                        tag,
                                                                    )
                                                                }
                                                                disabled={
                                                                    copyingTagId !==
                                                                        null ||
                                                                    contextLoading
                                                                }
                                                                style={{
                                                                    flexShrink: 0,
                                                                    textTransform:
                                                                        'none',
                                                                }}
                                                            >
                                                                {copyingTagId ===
                                                                tag.id
                                                                    ? 'Copying...'
                                                                    : 'Copy'}
                                                            </Button>
                                                        </ListItem>
                                                        <Collapse
                                                            in={isExpanded}
                                                            timeout="auto"
                                                        >
                                                            <Box
                                                                style={{
                                                                    paddingLeft: theme.spacing(
                                                                        5,
                                                                    ),
                                                                    paddingRight: theme.spacing(
                                                                        2,
                                                                    ),
                                                                    paddingBottom: theme.spacing(
                                                                        1.5,
                                                                    ),
                                                                    paddingTop: 0,
                                                                }}
                                                            >
                                                                <Box
                                                                    style={{
                                                                        backgroundColor:
                                                                            theme
                                                                                .palette
                                                                                .grey?.[50] ||
                                                                            theme
                                                                                .palette
                                                                                .action
                                                                                .hover,
                                                                        borderRadius:
                                                                            theme
                                                                                .shape
                                                                                .borderRadius,
                                                                        padding: theme.spacing(
                                                                            1,
                                                                            1.5,
                                                                        ),
                                                                        maxHeight: 120,
                                                                        overflow:
                                                                            'auto',
                                                                    }}
                                                                >
                                                                    {copyCount ===
                                                                    0 ? (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="textSecondary"
                                                                        >
                                                                            No
                                                                            properties
                                                                            you
                                                                            have
                                                                            access
                                                                            to.
                                                                        </Typography>
                                                                    ) : (
                                                                        <List
                                                                            dense
                                                                            disablePadding
                                                                        >
                                                                            {copyToApartmentIds.map(
                                                                                (
                                                                                    aptId,
                                                                                ) => {
                                                                                    const company = apartmentMap.get(
                                                                                        aptId,
                                                                                    )
                                                                                    return (
                                                                                        <ListItem
                                                                                            key={
                                                                                                aptId
                                                                                            }
                                                                                            disableGutters
                                                                                            style={{
                                                                                                paddingTop: 2,
                                                                                                paddingBottom: 2,
                                                                                            }}
                                                                                        >
                                                                                            <ListItemText
                                                                                                primary={
                                                                                                    company?.name ??
                                                                                                    `Property ${aptId}`
                                                                                                }
                                                                                                primaryTypographyProps={{
                                                                                                    variant:
                                                                                                        'caption',
                                                                                                }}
                                                                                            />
                                                                                        </ListItem>
                                                                                    )
                                                                                },
                                                                            )}
                                                                        </List>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Collapse>
                                                        {index <
                                                            filteredGlobalTags.length -
                                                                1 && (
                                                            <Divider component="li" />
                                                        )}
                                                    </React.Fragment>
                                                )
                                            },
                                        )}
                                    </List>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                </Box>

                {/* Tags List */}
                <Box
                    style={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        maxHeight: '400px',
                        overflow: 'auto',
                        marginBottom: theme.spacing(3),
                    }}
                >
                    {userTags.length === 0 ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{ padding: theme.spacing(3) }}
                        >
                            <Typography color="textSecondary">
                                No tags created yet. Click &quot;Add Tag&quot;
                                to create your first tag.
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {userTags.map((tag, index) => (
                                <React.Fragment key={tag.id}>
                                    <ListItem>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: theme.spacing(1) }}
                                        >
                                            <Box
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: tag.color,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                }}
                                            />
                                            <ListItemText
                                                primary={tag.name}
                                                secondary={
                                                    tag.apartments.length > 0
                                                        ? `${tag.apartments.length} properties`
                                                        : 'No properties assigned'
                                                }
                                            />
                                        </Box>
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    handleEditClick(tag)
                                                }
                                                size="small"
                                                style={{
                                                    marginRight: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    handleDeleteClick(tag)
                                                }
                                                size="small"
                                                style={{
                                                    color:
                                                        theme.palette.error
                                                            .main,
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < userTags.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Create/Edit Form */}
                {(isCreating || isEditing) && (
                    <Box
                        style={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.shape.borderRadius,
                            padding: theme.spacing(2),
                            backgroundColor: theme.palette.background.default,
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{ marginBottom: theme.spacing(2) }}
                        >
                            {isEditing ? 'Edit Tag' : 'Create New Tag'}
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Tag Name */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Tag Name"
                                    value={formData.name}
                                    onChange={(e) =>
                                        handleInputChange(
                                            'name',
                                            e.target.value,
                                        )
                                    }
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    required
                                />
                            </Grid>

                            {/* Color Picker */}
                            <Grid item xs={12}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: theme.spacing(2) }}
                                >
                                    <Typography variant="body2">
                                        Color:
                                    </Typography>
                                    <ColorPicker
                                        color={formData.color}
                                        setColor={(color) =>
                                            handleInputChange('color', color)
                                        }
                                        size={40}
                                    />
                                </Box>
                                {errors.color && (
                                    <Typography color="error" variant="caption">
                                        {errors.color}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Global Tag Switch */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_global_tag}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    'is_global_tag',
                                                    e.target.checked,
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Global tag"
                                />
                            </Grid>

                            {/* Property Selection */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="body2"
                                    style={{ marginBottom: theme.spacing(1) }}
                                >
                                    Select Properties
                                </Typography>

                                {/* Search Bar */}
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search properties..."
                                    value={propertySearch}
                                    onChange={(e) =>
                                        setPropertySearch(e.target.value)
                                    }
                                    style={{ marginBottom: theme.spacing(1) }}
                                    InputProps={{
                                        style: {
                                            borderRadius:
                                                theme.shape.borderRadius,
                                        },
                                    }}
                                />

                                <Box
                                    style={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: theme.shape.borderRadius,
                                        maxHeight: '200px',
                                        overflow: 'auto',
                                    }}
                                >
                                    {(() => {
                                        const availableProperties = getAvailableProperties()
                                        const filteredProperties = availableProperties.filter(
                                            (company) =>
                                                company.name
                                                    .toLowerCase()
                                                    .includes(
                                                        propertySearch.toLowerCase(),
                                                    ),
                                        )

                                        if (filteredProperties.length === 0) {
                                            return (
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    style={{
                                                        padding: theme.spacing(
                                                            2,
                                                        ),
                                                    }}
                                                >
                                                    <Typography
                                                        color="textSecondary"
                                                        variant="body2"
                                                    >
                                                        {propertySearch
                                                            ? 'No properties match your search'
                                                            : 'No properties in this organization'}
                                                    </Typography>
                                                </Box>
                                            )
                                        }

                                        return (
                                            <List dense>
                                                {filteredProperties.map(
                                                    (company) => {
                                                        const isSelected = formData.apartment_ids.includes(
                                                            company.id,
                                                        )

                                                        return (
                                                            <ListItem
                                                                key={company.id}
                                                                button
                                                                onClick={() => {
                                                                    if (
                                                                        isSelected
                                                                    ) {
                                                                        handleInputChange(
                                                                            'apartment_ids',
                                                                            formData.apartment_ids.filter(
                                                                                (
                                                                                    id,
                                                                                ) =>
                                                                                    id !==
                                                                                    company.id,
                                                                            ),
                                                                        )
                                                                    } else {
                                                                        handleInputChange(
                                                                            'apartment_ids',
                                                                            [
                                                                                ...formData.apartment_ids,
                                                                                company.id,
                                                                            ],
                                                                        )
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon>
                                                                    <Checkbox
                                                                        checked={
                                                                            isSelected
                                                                        }
                                                                        edge="start"
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={
                                                                        company.name
                                                                    }
                                                                    secondary={`${
                                                                        company.billable_unit_count ||
                                                                        0
                                                                    } units`}
                                                                />
                                                            </ListItem>
                                                        )
                                                    },
                                                )}
                                            </List>
                                        )
                                    })()}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            {(isCreating || isEditing) && (
                <DialogActions
                    style={{
                        padding: theme.spacing(2, 3),
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button onClick={resetForm} disabled={contextLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={contextLoading || !formData.name.trim()}
                        startIcon={
                            contextLoading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <AddIcon />
                            )
                        }
                    >
                        {contextLoading
                            ? isEditing
                                ? 'Updating...'
                                : 'Creating...'
                            : isEditing
                            ? 'Update Tag'
                            : 'Create Tag'}
                    </Button>
                </DialogActions>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: theme.shape.borderRadius * 2,
                    },
                }}
            >
                <DialogTitle style={{ paddingBottom: 8 }}>
                    <Box display="flex" alignItems="center">
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width={40}
                            height={40}
                            borderRadius="50%"
                            bgcolor="error.light"
                            mr={2}
                        >
                            <WarningIcon
                                style={{ color: 'white', fontSize: 20 }}
                            />
                        </Box>
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Delete Tag
                        </Typography>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent style={{ paddingTop: 24, paddingBottom: 24 }}>
                    <Typography variant="body1" style={{ marginBottom: 16 }}>
                        Are you sure you want to delete the tag &quot;
                        {tagToDelete?.name}&quot;?
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        This action cannot be undone. The tag will be
                        permanently removed and all associated properties will
                        be unassigned.
                    </Typography>
                </DialogContent>

                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button
                        onClick={handleCancelDelete}
                        disabled={contextLoading}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        disabled={contextLoading}
                        variant="contained"
                        color="secondary"
                        startIcon={
                            contextLoading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <DeleteIcon />
                            )
                        }
                    >
                        {contextLoading ? 'Deleting...' : 'Delete Tag'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    )
}
